import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { vehiclesApi, type VehicleQuery } from '@/lib/api/endpoints';
import { queryKeys } from './keys';
import type { CreateVehicleInput, Id, UpdateVehicleInput } from '@/types/api';

export function useVehicles(query?: VehicleQuery) {
  return useQuery({
    queryKey: queryKeys.vehicles.list(query),
    queryFn: () => vehiclesApi.list(query),
    placeholderData: (previous) => previous,
  });
}

/**
 * The map's baseline. The socket carries every change afterwards, so this only
 * refetches on a long interval as a self-heal for missed events.
 */
export function useLiveVehicles() {
  return useQuery({
    queryKey: queryKeys.vehicles.live(),
    queryFn: () => vehiclesApi.live(),
    staleTime: 15_000,
    refetchInterval: 120_000,
  });
}

/**
 * Full vehicle records keyed by id.
 *
 * `/vehicles/live` is a deliberately trimmed projection — plate, make, model,
 * status, group, lastKnown, deviceId — so the map's own data has no driver,
 * odometer, year or VIN. This fetches the complete list once (with driver and
 * device populated) and caches it, so hover cards and the map's detail panel
 * can show those fields without a request per vehicle.
 */
export function useVehicleDirectory() {
  return useQuery({
    queryKey: queryKeys.vehicles.list({ limit: 200 }),
    queryFn: () => vehiclesApi.list({ limit: 200 }),
    staleTime: 5 * 60_000,
    select: (page) => {
      const byId = new Map<Id, (typeof page.items)[number]>();
      for (const vehicle of page.items) byId.set(vehicle.id, vehicle);
      return { items: page.items, byId };
    },
  });
}

export function useVehicle(id: Id | null | undefined) {
  return useQuery({
    queryKey: queryKeys.vehicles.detail(id ?? ''),
    queryFn: () => vehiclesApi.get(id as Id),
    enabled: Boolean(id),
  });
}

export function useOdometerHistory(id: Id | null | undefined) {
  return useQuery({
    queryKey: queryKeys.vehicles.odometer(id ?? ''),
    queryFn: () => vehiclesApi.odometerHistory(id as Id),
    enabled: Boolean(id),
  });
}

export function useCreateVehicle() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateVehicleInput) => vehiclesApi.create(input),
    onSuccess: (vehicle) => {
      client.invalidateQueries({ queryKey: queryKeys.vehicles.all });
      client.invalidateQueries({ queryKey: queryKeys.drivers.all });
      toast.success(`${vehicle.plateNumber} added to the fleet`);
    },
  });
}

export function useUpdateVehicle() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: Id; input: UpdateVehicleInput }) =>
      vehiclesApi.update(id, input),
    onSuccess: (vehicle) => {
      client.invalidateQueries({ queryKey: queryKeys.vehicles.all });
      client.invalidateQueries({ queryKey: queryKeys.drivers.all });
      client.setQueryData(queryKeys.vehicles.detail(vehicle.id), vehicle);
      toast.success(`${vehicle.plateNumber} updated`);
    },
  });
}

export function useDecommissionVehicle() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: Id) => vehiclesApi.decommission(id),
    onSuccess: (vehicle) => {
      client.invalidateQueries({ queryKey: queryKeys.vehicles.all });
      toast.success(`${vehicle.plateNumber} decommissioned`);
    },
  });
}

export function useAdjustOdometer(id: Id) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { valueKm: number; reason: string }) => vehiclesApi.adjustOdometer(id, input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.vehicles.detail(id) });
      client.invalidateQueries({ queryKey: queryKeys.vehicles.odometer(id) });
      toast.success('Odometer corrected — the change is in the audit log');
    },
  });
}
